import { useMemo } from 'react';

interface MoonPhaseProps {
  gregorianDate?: Date;
  size?: number;
  showLabel?: boolean;
  compact?: boolean;
}

// Mean synodic month in days.
const SYNODIC_MONTH = 29.530588853;
const KNOWN_NEW_MOON = new Date('2012-12-13T08:42:00Z').getTime();

// Calculate the real lunar phase based on Gregorian date
const getRealMoonPhase = (date: Date) => {
  const now = date.getTime();
  const daysSinceKnownNewMoon = (now - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
  
  // Current position in the lunar cycle (0 to 1)
  let lunarCycle = (daysSinceKnownNewMoon % SYNODIC_MONTH) / SYNODIC_MONTH;
  
  // Normalize to 0-1 range
  if (lunarCycle < 0) lunarCycle += 1;
  
  const phase = lunarCycle;
  
  // Illumination: 0% at new moon (phase 0), 100% at full moon (phase 0.5)
  const illumination = Math.round((1 - Math.cos(phase * 2 * Math.PI)) / 2 * 100);
  
  // Determine phase name
  let name: string;
  let shortName: string;
  
  if (phase < 0.025 || phase >= 0.975) {
    name = 'New Moon'; shortName = 'New';
  } else if (phase < 0.25) {
    name = 'Waxing Crescent'; shortName = 'Wax Cres';
  } else if (phase < 0.275) {
    name = 'First Quarter'; shortName = '1st Qtr';
  } else if (phase < 0.475) {
    name = 'Waxing Gibbous'; shortName = 'Wax Gib';
  } else if (phase < 0.525) {
    name = 'Full Moon'; shortName = 'Full';
  } else if (phase < 0.75) {
    name = 'Waning Gibbous'; shortName = 'Wan Gib';
  } else if (phase < 0.775) {
    name = 'Last Quarter'; shortName = 'Last Qtr';
  } else {
    name = 'Waning Crescent'; shortName = 'Wan Cres';
  }
  
  const isWaxing = phase < 0.5;
  
  return { phase, illumination, name, shortName, isWaxing };
};

// SVG Moon component - simple and accurate
function MoonSVG({ phase, size }: { phase: number; size: number }) {
  const radius = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;
  
  // Calculate how much of the moon is lit (0 to 1)
  const litFraction = (1 - Math.cos(phase * 2 * Math.PI)) / 2;
  
  // The terminator ellipse width
  // At quarters (0.25, 0.75): terminatorWidth = 0 (straight line)
  // At new/full (0, 0.5): terminatorWidth = radius (at edge)
  const terminatorWidth = Math.abs(Math.cos(phase * 2 * Math.PI)) * radius;
  
  // Determine arc sweep direction based on phase quadrant
  // Crescent phases (0-0.25, 0.75-1): terminator bulges AWAY from lit side
  // Gibbous phases (0.25-0.5, 0.5-0.75): terminator bulges TOWARD dark side
  const isWaxingCrescent = phase > 0 && phase < 0.25;
  const isWaxingGibbous = phase >= 0.25 && phase < 0.5;
  const isWaningGibbous = phase >= 0.5 && phase < 0.75;
  const isWaningCrescent = phase >= 0.75 && phase < 1;
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id={`moonLit-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f5f5f0" />
          <stop offset="50%" stopColor="#e8e8e0" />
          <stop offset="100%" stopColor="#d0d0c8" />
        </linearGradient>
      </defs>
      
      {/* Dark moon base */}
      <circle 
        cx={cx} 
        cy={cy} 
        r={radius} 
        fill="#1a1a2e"
        stroke="#2a2a4e"
        strokeWidth="1"
      />
      
      {/* Lit portion */}
      {litFraction > 0.01 && litFraction < 0.99 && (
        <>
          {phase < 0.5 ? (
            // Waxing: lit on RIGHT side
            <path
              d={`
                M ${cx} ${cy - radius}
                A ${radius} ${radius} 0 0 1 ${cx} ${cy + radius}
                A ${terminatorWidth} ${radius} 0 0 ${isWaxingCrescent ? 0 : 1} ${cx} ${cy - radius}
              `}
              fill={`url(#moonLit-${size})`}
            />
          ) : (
            // Waning: lit on LEFT side
            <path
              d={`
                M ${cx} ${cy - radius}
                A ${radius} ${radius} 0 0 0 ${cx} ${cy + radius}
                A ${terminatorWidth} ${radius} 0 0 ${isWaningCrescent ? 1 : 0} ${cx} ${cy - radius}
              `}
              fill={`url(#moonLit-${size})`}
            />
          )}
        </>
      )}
      
      {/* Full moon */}
      {litFraction >= 0.99 && (
        <circle cx={cx} cy={cy} r={radius} fill={`url(#moonLit-${size})`} />
      )}
      
      {/* Crater details on lit portion */}
      {litFraction > 0.2 && (
        <>
          <circle cx={cx - radius * 0.25} cy={cy - radius * 0.15} r={radius * 0.12} fill="#c8c8c0" opacity="0.3" />
          <circle cx={cx + radius * 0.15} cy={cy + radius * 0.25} r={radius * 0.08} fill="#c8c8c0" opacity="0.2" />
        </>
      )}
    </svg>
  );
}

export function MoonPhase({ gregorianDate, size = 40, showLabel = true, compact = false }: MoonPhaseProps) {
  const moonData = useMemo(() => {
    const date = gregorianDate || new Date();
    return getRealMoonPhase(date);
  }, [gregorianDate]);

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <MoonSVG phase={moonData.phase} size={size} />
        {showLabel && (
          <div className="text-xs">
            <p className="font-medium text-foreground leading-tight">{moonData.shortName}</p>
            <p className="text-muted-foreground leading-tight">{moonData.illumination}%</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <MoonSVG phase={moonData.phase} size={size} />
      {showLabel && (
        <div className="text-center">
          <p className="text-[10px] font-medium text-foreground leading-tight">{moonData.name}</p>
          <p className="text-[9px] text-muted-foreground leading-tight">{moonData.illumination}%</p>
        </div>
      )}
    </div>
  );
}
